import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import {
  deliveryMaterialDTO,
  ListStockPartsDeliveryData,
  ListStockPartsDeliveryDataSupport,
} from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import {
  Button,
  Col,
  Form,
  FormInstance,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import ModalConsumableSelect from './ModalConsumableSelect';
import ModalWorkOrderSelect from './ModalWorkOrderSelect';
import PartsAndConsumablesList from './PartsAndConsumablesList';
import ModalPartsSelect from './ModalPartsSelect';

interface Props {
  form: FormInstance<deliveryMaterialDTO>;
  deliveredItems: ListStockPartsDeliveryData[];
  setDeliveredItems: React.Dispatch<
    React.SetStateAction<ListStockPartsDeliveryData[]>
  >;
}

const DeliveryMaterialForm = ({
  form,
  setDeliveredItems,
  deliveredItems,
}: Props) => {
  const { Title } = Typography;
  const intl = useIntl();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const companyId = Form.useWatch('companyId', form);
  const deliverymanId = Form.useWatch('deliverymanId', form);

  const workorderConfigurationCompany = useWorkorderConfigurationCompany();
  const isAutomaticStock = workorderConfigurationCompany.data?.isAutomaticStock;

  const [partOpen, partAction] = useDisclosure(false);
  const [consumableWorkOrderkOpen, consumableWorkOrderAction] =
    useDisclosure(false);
  const [consumableNoLinkOpen, consumableNoLinkAction] = useDisclosure(false);

  const [consumablesSelected, setConsumablesSelected] = useState<
    ListStockPartsDeliveryDataSupport[]
  >([]);
  const [partsSelected, setPartsSelected] = useState<
    ListStockPartsDeliveryDataSupport[]
  >([]);

  const mdSizeDefault = isAutomaticStock === false ? 8 : 12;

  useEffect(() => {
    if (companyId) {
      (async () => {
        await workorderConfigurationCompany.mutateAsync(companyId);
      })();
    }
  }, [companyId]);

  useEffect(() => {
    const combinedItems = [...partsSelected, ...consumablesSelected];

    setDeliveredItems(combinedItems);

    form.setFieldValue('deliveredItems', combinedItems);
  }, [partsSelected, consumablesSelected]);

  const mergeUniqueItems = (
    prevItems: ListStockPartsDeliveryDataSupport[],
    newItems: ListStockPartsDeliveryDataSupport[],
    key: keyof ListStockPartsDeliveryDataSupport,
  ) => {
    const uniqueItems = newItems.filter(
      (newItem) =>
        !prevItems.some((prevItem) => prevItem[key] === newItem[key]),
    );
    return [...prevItems, ...uniqueItems];
  };

  const handlerConsumablesSelect = (
    consumables: ListStockPartsDeliveryDataSupport[],
  ) => {
    setConsumablesSelected((prev) =>
      mergeUniqueItems(prev, consumables, 'consumableId'),
    );
  };

  const handlerPartsSelect = (parts: ListStockPartsDeliveryDataSupport[]) => {
    setPartsSelected((prev) =>
      mergeUniqueItems(prev, parts, 'workOrderItemId'),
    );
  };

  const handleSetDeliveredItems = (
    items: ListStockPartsDeliveryDataSupport[],
  ) => {
    setDeliveredItems(items);
    form.setFieldValue('deliveredItems', items);

    // Sincroniza os estados para remover os itens excluídos
    setPartsSelected((prev) =>
      prev.filter((item) => items.some((i) => i.partId === item.partId)),
    );
    setConsumablesSelected((prev) =>
      prev.filter((item) =>
        items.some((i) => i.consumableId === item.consumableId),
      ),
    );
  };

  return (
    <>
      <Form form={form}>
        <Row gutter={[16, 9]}>
          <Col xs={24} md={12} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.deliveryData',
              })}
              name="deliveryDateMoment"
              initialValue={moment()}
            >
              <InputDate />
            </FloatingFormItem>
          </Col>

          <Col xs={24} md={12} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
              getValueFromEvent={(value) => {
                form.resetFields(['deliverymanId', 'receiverId']);
                return value;
              }}
            >
              <CompaniesInfiniteSelectField
                allowClear
                queryEnable
                disabled={deliveredItems.length > 0}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} md={12} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.deliveredBy',
              })}
              name="deliverymanId"
              rules={[defaultEmptyFieldError]}
              getValueFromEvent={(value) => {
                form.setFieldValue('receiverId', undefined);
                return value;
              }}
            >
              <EmployeesInfiniteSelectField
                queryFilter={{ companyId }}
                disabled={!companyId}
                allowClear
                selectSingle={false}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} md={12} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.receivedBy',
              })}
              name="receiverId"
              rules={[defaultEmptyFieldError]}
            >
              <EmployeesInfiniteSelectField
                queryFilter={{ companyId }}
                disabled={!companyId}
                excludedId={deliverymanId}
                selectSingle={false}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} style={{ textAlign: 'right' }}>
            <Space>
              <Row gutter={[24, 24]}>
                {!isAutomaticStock && (
                  <Col span={24} md={mdSizeDefault}>
                    <Button
                      disabled={!companyId}
                      type="primary"
                      block
                      onClick={partAction.open}
                    >
                      {intl.formatMessage({
                        id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.button.includeParts',
                      })}
                    </Button>
                  </Col>
                )}

                <Col span={24} md={mdSizeDefault}>
                  <Tooltip
                    placement="top"
                    title="Inclusão de insumos com vínculo no orçamento"
                  >
                    <Button
                      type="primary"
                      disabled={!companyId}
                      block
                      onClick={consumableWorkOrderAction.open}
                    >
                      {intl.formatMessage({
                        id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.button.includeConsumablesCv',
                      })}
                    </Button>
                  </Tooltip>
                </Col>

                <Col span={24} md={mdSizeDefault}>
                  <Tooltip
                    placement="top"
                    title="Inclusão de insumos sem vínculo no orçamento"
                  >
                    <Button
                      type="primary"
                      disabled={!companyId}
                      block
                      onClick={consumableNoLinkAction.open}
                    >
                      {intl.formatMessage({
                        id: 'deliveryOrReturnOfMaterials.deliverMaterial.form.button.includeConsumablesSv',
                      })}
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
            </Space>
          </Col>

          <Col span={24}>
            <Title level={5}>
              {intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.deliverMaterial.TitleTable.itemsToDeliver',
              })}
            </Title>

            <PartsAndConsumablesList
              selectedItems={
                deliveredItems as ListStockPartsDeliveryDataSupport[]
              }
              setDeliveredItems={handleSetDeliveredItems}
            />
          </Col>
        </Row>
      </Form>

      <ModalPartsSelect
        companyId={companyId}
        open={partOpen}
        onCancel={partAction.close}
        partsSelected={partsSelected}
        handlerPartsSelect={handlerPartsSelect}
      />

      <ModalWorkOrderSelect
        companyId={companyId}
        open={consumableWorkOrderkOpen}
        onCancel={consumableWorkOrderAction.close}
        consumablesSelected={consumablesSelected}
        handlerConsumablesSelect={handlerConsumablesSelect}
      />

      <ModalConsumableSelect
        companyId={companyId}
        open={consumableNoLinkOpen}
        onCancel={consumableNoLinkAction.close}
        isConsumableNoLink={consumableNoLinkOpen}
        consumablesSelected={consumablesSelected}
        handlerConsumablesSelect={handlerConsumablesSelect}
      />
    </>
  );
};

export default DeliveryMaterialForm;
